import {extendObservable, computed, toJS, isObservableArray} from 'mobx'
import firebase from '../firebase'
import * as helpers from '../lib/HelperFunctions'
import Logger from './Logger'

export const INVITEE_PHOTOURL = 'https://ui-avatars.com/api/?name=?&color=707070&background=cccccc&size=60'

export const listFilterModes = Object.freeze({
    allIdeas: 0,
    workOn: 1,
    responsibleFor: 2,
    teamIdeas: 3,
})

export const dueDateModes = Object.freeze({
    sortBy: 0,
    byWeek: 1,
    byMonth: 2,
    byYear: 3,
})

const DEFAULT_STAGE = 0

const DEFAULT_SLIDES_BEFORE_TOOLS = [
        {show: true, name: 'intro'},
        {show: true, name: 'team'},
        {show: true, name: 'need'},
        {show: true, name: 'stages'},
        {show: true, name: 'recommendation'},
        {show: true, name: 'images'},
        {show: true, name: 'sketch'},
        {show: true, name: 'richText'}]
const DEFAULT_SLIDES_AFTER_TOOLS = [
        {show: true, name: 'timeline'},
        {show: true, name: 'viability'},
        {show: true, name: 'outro'}]

const EMPTY_IDEA = {
    name: '',
    description: '',
    need: '',
    needImageUrl: '',
    needImageUrl_bucket: '',
    needImageUrl_fullPath: '',
    needOrigFileName: '',
    moreDescription: '',
    effortRating: 0,
    impactRating: 0,
    imageUrl: '',
    imageUrl_bucket: '',
    imageUrl_fullPath: '',
    imageOrigFileName: '',
    imageUrls: [],
    imageUrls_bucket: [],
    imageUrls_fullPath: [],
    imageOrigFileNames: [],
    attachmentUrl: '',
    attachmentUrls: [],
    attachmentUrls_bucket: [],
    attachmentUrls_fullPath: [],
    attachmentOrigFileNames: [],
    imageText: '',
    attachmentText: '',
    sketchText: '',
    dueDate: '',
    team: [],
    tags: [],
    currentStage: DEFAULT_STAGE,
    slideBgColor: '#1B1B18',
    slideHeaderColor: '#ED1558',
    slideTextColor: '#F5F1EB',
    whichTemplate: 1,
    showQuestions: false,
    valueCreationTool: {},
    costRows: [],
    revenueRows: [],
    viabilityText: '',
    timelineRows: [],
    activeTimelineStep: 0,
}
class IdeaStore {
    constructor(authStore, tools, notificationStore, prefStore) {
        this.authStore = authStore
        this.prefStore = prefStore
        this.toolStore = tools
        this.notificationStore = notificationStore
        extendObservable(this, {
            defaultSlides: computed(() =>{
                let dS = []
                dS = dS.concat(DEFAULT_SLIDES_BEFORE_TOOLS)
                for(let toolName of tools.toolOrder){
                    dS.push({show: true, name: toolName})
                }
                return dS.concat(DEFAULT_SLIDES_AFTER_TOOLS)
            }),
            emptyIdea: computed(() =>{
                let eI = Object.assign({},EMPTY_IDEA)
                eI['slides'] = this.defaultSlides
                for (const tool of tools.toolOrder)
                    eI[tool] = {}
                return eI
            }),
            theIdea: this.emptyIdea,
            ideaKey: null,
            theIdeaStatus: 0,
            theIdeaLastEditTimer: null,
            sketch: null,
            newIdea: false,
            manuallyChangedTeams: false,
            sketchChecksum: '',
            userTeamKey: '',
            listOfIdeaKeys: [],
            ownedIdeas: {},
            sharedIdeas: {},
            teamSharedIdeas: {},
            storedOwnedIdeas: {},
            storedSharedIdeas: {},
            storedTeamSharedIdeas: {},
            storedResponsibleIdeas: {},
            ideas: {},
            teamSharedIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.teamSharedIdeas)
                }
            }),
            filteredTeamSharedIdeaArray: computed(() =>{
                //return null until loading is done
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.filter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.filter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.filter, 'i')
                    }
                    return this.teamSharedIdeaArray
                        .filter(idea => (!this.filter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)))
                        .sort(helpers.compareByName)
                }
            }),
            teamSharedStoredIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.storedTeamSharedIdeas).filter(responsibleIdea => (responsibleIdea.hasOwnProperty('status')))
                }
            }),
            filteredTeamSharedStoredIdeaArray: computed(() =>{
                //return null until loading is done
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.storedFilter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.storedFilter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.storedFilter, 'i')
                    }
                    return this.teamSharedStoredIdeaArray
                        .filter(idea => (!this.storedFilter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)))
                        .sort(helpers.compareByName)
                }
            }),
            sharedIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.sharedIdeas)
                }
            }),
            sharedStoredIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.storedSharedIdeas)
                }
            }),
            ownedIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.ownedIdeas)
                }
            }),
            ownedStoredIdeaArray: computed(() => {
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return helpers.toArray(this.storedOwnedIdeas)
                }
            }),
            allIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return this.ownedIdeaArray.concat(this.sharedIdeaArray).concat(this.teamSharedIdeaArray)
                    .filter((obj, pos, arr) => { //remove objects with duplicate keys, from https://ilikekillnerds.com/2016/05/removing-duplicate-objects-array-property-name-javascript/
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                }
            }),
            filteredAllIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.filter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.filter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.filter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.allIdeas
                    .filter(idea => ((!this.filter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)) && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId) >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            allStoredIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return this.ownedStoredIdeaArray.concat(this.sharedStoredIdeaArray).concat(this.teamSharedStoredIdeaArray)
                    .filter((obj, pos, arr) => { //remove objects with duplicate keys, from https://ilikekillnerds.com/2016/05/removing-duplicate-objects-array-property-name-javascript/
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                }
            }),
            filteredAllStoredIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.storedFilter){
                        //var invalidChars = /[°"§%()[\]{}=\\?´`'#<>|;:+_-]+/g
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.storedFilter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.storedFilter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamStoredIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamStoredIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.allStoredIdeas
                    .filter(idea => ((!this.filter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)) && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId)  >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            responsibleIdeaArray: computed(() => {
                const authUser = this.authStore.authUser()
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    //If the idea has a responsible that is not the owner, don't show it
                    return helpers.toArray(this.allIdeas).filter(responsibleIdea => (responsibleIdea.hasOwnProperty('responsible') && responsibleIdea.responsible.email === authUser.email) ||
                    (responsibleIdea.owner === authUser.email && !responsibleIdea.hasOwnProperty('responsible')))
                    .filter((obj, pos, arr) => {
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                }
            }),
            filteredResponsibleIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.filter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.filter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.filter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.responsibleIdeaArray
                    .filter(idea => ((!this.filter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)) && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId) >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            responsibleStoredIdeaArray: computed(() => {
                const authUser = this.authStore.authUser()
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    //If the idea has a responsible that is not the owner, don't show it
                    return helpers.toArray(this.allStoredIdeas).filter(responsibleIdea => (responsibleIdea.hasOwnProperty('responsible') && responsibleIdea.responsible.email === authUser.email && responsibleIdea.hasOwnProperty('status')) ||
                    (responsibleIdea.owner === authUser.email && !responsibleIdea.hasOwnProperty('responsible') && responsibleIdea.hasOwnProperty('status')))
                    .filter((obj, pos, arr) => {
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                }
            }),
            filteredStoredResponsibleIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.storedFilter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.storedFilter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.storedFilter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamStoredIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamStoredIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.responsibleStoredIdeaArray
                    .filter(idea => ((!this.storedFilter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)) && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId) >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            workOnIdeaArray: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    return this.responsibleIdeaArray.concat(this.sharedIdeaArray)
                    .filter((obj, pos, arr) => { //remove objects with duplicate keys, from https://ilikekillnerds.com/2016/05/removing-duplicate-objects-array-property-name-javascript/
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                }
            }),
            filteredWorkOnIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.filter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.filter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.filter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.workOnIdeaArray
                    .filter(idea => ((!this.filter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags)) && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId) >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            workOnStoredIdeaArray: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let workOnFilter = this.responsibleStoredIdeaArray.concat(this.sharedStoredIdeaArray)
                    .filter((obj, pos, arr) => { //remove objects with duplicate keys, from https://ilikekillnerds.com/2016/05/removing-duplicate-objects-array-property-name-javascript/
                        return arr.map(mapObj => mapObj['key']).indexOf(obj['key']) === pos
                    })
                    return workOnFilter.filter(responsibleIdea => (responsibleIdea.hasOwnProperty('status')))
                }
            }),
            filteredWorkOnStoredIdeas: computed(() =>{
                if (this.loadingIdeas === true){
                    return null
                } else if (this.loadingIdeas === false){
                    let matchesFilter = null
                    if (this.storedFilter){
                        var invalidChars = /[()[\]\\?+--*]+/g
                        var cleanedUpFilter = this.storedFilter.replace(invalidChars, "")
                        matchesFilter = new RegExp(cleanedUpFilter, 'i')
                    } else {
                        matchesFilter = new RegExp(this.storedFilter, 'i')
                    }
                    let showTheIdeas = 3
                    if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('prefs') && this.authStore.userProfile().prefs.hasOwnProperty('showNonTeamStoredIdeas')){
                        showTheIdeas = this.authStore.userProfile().prefs.showNonTeamStoredIdeas
                    }
                    let showDifferentTeamIdeas = -2
                    if (showTheIdeas === 3){
                        showDifferentTeamIdeas = -2
                    } else {
                        showDifferentTeamIdeas = 0
                    }
                    let assignedTeams = {}
                    if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                        assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                    }
                    let assignedTeamKeys = []
                    for (let teamIndex in assignedTeams){
                        assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                    }
                    return this.workOnStoredIdeaArray
                    .filter(idea => ((!this.storedFilter || matchesFilter.test(idea.name) || matchesFilter.test(idea.tags))  && (idea.hasOwnProperty('teamId') !== showTheIdeas && (assignedTeamKeys.indexOf(idea.teamId) >= showDifferentTeamIdeas || assignedTeamKeys.length === 0))))
                    .sort(helpers.compareByName)
                }
            }),
            filter: computed(() => {
            if (this.prefStore.hasOwnProperty('listSearchText') && this.prefStore.listSearchText !== '')
                return this.prefStore.listSearchText
            }),
            storedFilter: computed(() => {
                if (this.prefStore.hasOwnProperty('listSearchText') && this.prefStore.storedListSearchText !== '')
                    return this.prefStore.storedListSearchText
                }),
            loadingSingleIdea: false,
            loadingOwnIdeas: false,
            loadingSharedIdeas: false,
            loadingTeamSharedIdeas: false,
            loadingIdeas: computed(() =>{
                return this.loadingOwnIdeas || this.loadingSharedIdeas || this.loadingTeamSharedIdeas
            }),
            showSpinner: computed(() =>{
                return this.loadingIdeas || this.loadingSingleIdea
            }),
            online: true,
            saving: false,
            savingRichField: false, //TODO: we should get rid of this
            redirectUrl: '/',
            isLoggedIn: false,
            errorAlert: null,
            infoAlert: null,
            savingTimer: null,
        })

        this.unwatchAuth = authStore.watchAuth(user => {
            if(user){
                this.subscribeToAllIdeas()
            }
        })
    }
    //LOAD IDEAS
    subscribeToMyIdeas(){
        const authUser = this.authStore.authUser()
        const rootRef = firebase.database().ref()
        let thing = this
        let listOfIdeas = []
        firebase.database().ref().child('users/' + authUser.uid).once('value', userSnap => {
            let user = userSnap.val()
            firebase.database().ref().child('users/' + authUser.uid + '/myIdeas').on('value', snap => {
                if (snap.exists()) {
                    let myIdeas = snap.val()
                    if(user.hasOwnProperty('primaryTeam')){
                        thing.userTeamKey = user.primaryTeam
                    } else {
                        thing.userTeamKey = ''
                    }
                    let numIdeas = 0
                    if(myIdeas){
                        numIdeas = Object.keys(myIdeas).length
                    }
                    thing.loadingOwnIdeas = true
                    if(numIdeas === 0){
                        this.loadingOwnIdeas = false
                        return
                    }
                    let i = 0
                    for(let ideaKey in myIdeas){
                        // console.log('subscribeToMyIdeas Looping over ideas',ideaKey)
                        if(listOfIdeas.indexOf(ideaKey) < 0){
                            listOfIdeas.push(ideaKey)
                        }
                        /* console.log('loading my ideas', thing.ownedIdeas) */
                        if(!thing.ownedIdeas.hasOwnProperty(ideaKey)){
                            //eslint-disable-next-line no-loop-func
                            rootRef.child('ideas/'+ideaKey).on('value', snap2 => {
                                // console.log('subscribeToMyIdeas INNER '+ideaKey+'.on(value) triggered')
                                i++
                                if(snap2.exists()){
                                    let idea = Object.assign(this.emptyIdea,snap2.val())
                                    if((idea.hasOwnProperty('teamId') && idea.teamId === thing.userTeamKey) || (idea.hasOwnProperty('teamId') === false)){
                                        if(idea.hasOwnProperty('status')){
                                            thing.storedOwnedIdeas = Object.assign({},thing.storedOwnedIdeas,{[ideaKey]: idea})
                                        } else {
                                            thing.ownedIdeas = Object.assign({},thing.ownedIdeas,{[ideaKey]: idea})
                                        }
                                    }
                                }
                                if(i === numIdeas){
                                    thing.loadingOwnIdeas = false
                                }
                            })
                        }else{
                            i++
                            if(i === numIdeas){
                                thing.loadingOwnIdeas = false
                            }
                        }
                    }
                } else {
                    this.loadingOwnIdeas = false
                }
            })
        })
    }
    subscribeToIdeasSharedWithMePersonally(){
        const authUser = this.authStore.authUser()
        const rootRef = firebase.database().ref()
        this.loadingSharedIdeas = true
        let thing = this
        firebase.database().ref().child('users/' + authUser.uid).once('value', userSnap => {
            let user = userSnap.val()
            firebase.database().ref().child('users/' + authUser.uid + '/ideas').on('value', snap => {
                if (snap.exists()) {
                let ideas = snap.val()
                    let numIdeas = 0
                    if(ideas){
                        numIdeas = Object.keys(ideas).length
                    }
                    thing.loadingSharedIdeas = true
                    if(numIdeas === 0){
                        thing.loadingSharedIdeas = false
                        return
                    }
                    let i = 0
                    for(let ideaKey in ideas){
                        if(!thing.sharedIdeas.hasOwnProperty(ideaKey)){
                            //eslint-disable-next-line no-loop-func
                            rootRef.child('ideas/'+ideaKey).on('value', snap2 => {
                                i++
                                let idea = Object.assign(this.emptyIdea,snap2.val())
                                thing.updateTeamInfoInIdeas(snap2.val())
                                let assignedTeams = {}
                                if (this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('assignedTeams')){
                                    assignedTeams = toJS(this.authStore.userProfile().assignedTeams)
                                }
                                let assignedTeamKeys = []
                                for (let teamIndex in assignedTeams){
                                    assignedTeamKeys[teamIndex] = assignedTeams[teamIndex].teamKey
                                }
                                if((idea.hasOwnProperty('teamId') && idea.teamId === user.primaryTeam) || (idea.hasOwnProperty('teamId') === false) || (idea.hasOwnProperty('teamId') && (assignedTeamKeys.indexOf(idea.teamId) === -1 || assignedTeamKeys.length === 0))){
                                    if (idea.hasOwnProperty('status')){
                                        thing.storedSharedIdeas = Object.assign({},thing.storedSharedIdeas,{[ideaKey]: idea})
                                    } else {
                                        thing.sharedIdeas = Object.assign({},thing.sharedIdeas,{[ideaKey]: idea})
                                    }
                                }
                                if(i === numIdeas){
                                    thing.loadingSharedIdeas = false
                                }
                            })
                        }else{
                            i++
                            if(i === numIdeas){
                                thing.loadingSharedIdeas = false
                            }
                        }
                    }
                } else {
                    this.loadingSharedIdeas = false
                }
            })
        })
    }
    //Set the photoURL under team in the ideas the user is invited to
    //TODO: do we still need this, if so can we move it to a cloud function?
    //this is a hint that our sharing model with emails as keys is not optimal
    updateTeamInfoInIdeas(sharedIdea) {
        const rootRef = firebase.database().ref()
        const authUser = this.authStore.authUser()
        for (let ssi in sharedIdea) {
            if (sharedIdea[ssi].hasOwnProperty('team')) {
                let ssit = sharedIdea[ssi].team
                for (let ssitm in ssit) {
                    if (ssit[ssitm].hasOwnProperty('email')) {
                        if (ssit[ssitm].email === authUser.email) {
                            if (!ssit[ssitm].hasOwnProperty('photoURL')) {
                                //write if no photoURL is set
                                ssit[ssitm].photoURL = authUser.photoURL
                                rootRef.child('ideas/' + ssi + '/team/' + ssitm).update(ssit[ssitm])
                            } else {
                                //overwrite if only default photoURL is still set
                                if (ssit[ssitm].photoURL === INVITEE_PHOTOURL) {
                                    ssit[ssitm].photoURL = authUser.photoURL
                                    rootRef.child('ideas/' + ssi + '/team/' + ssitm).update(ssit[ssitm])
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    subscribeToIdeasSharedWithTeam() {
        const authUser = this.authStore.authUser()
        const rootRef = firebase.database().ref()
        this.loadingTeamSharedIdeas = true
        firebase.database().ref().child('users/' + authUser.uid + '/primaryTeam').on('value', userSnapTeam => {
            if (userSnapTeam.exists()) {
                let userTeam = userSnapTeam.val()
                let thing = this
                thing.ownedIdeas = {}
                thing.sharedIdeas = {}
                thing.teamSharedIdeas = {}
                thing.storedOwnedIdeas = {}
                thing.storedSharedIdeas = {}
                thing.storedResponsibleIdeas = {}
                thing.storedTeamSharedIdeas = {}
                firebase.database().ref().child('users/' + authUser.uid + '/myIdeas').off()
                firebase.database().ref().child('users/' + authUser.uid + '/ideas').off()
                thing.subscribeToMyIdeas()
                thing.subscribeToIdeasSharedWithMePersonally()
                rootRef.child('teams/' + userTeam + '/ideas').orderByKey().on('value', snap => {
                        // thing.teamSharedIdeas = {}
                    const numIdeas = snap.numChildren()
                    if (numIdeas === 0) {
                        thing.loadingTeamSharedIdeas = false
                        return
                    }
                    let i = 0
                    for (let ideaKey in snap.val()) {
                        if(!thing.teamSharedIdeas.hasOwnProperty(ideaKey)){
                            //eslint-disable-next-line no-loop-func
                            rootRef.child('ideas/' + ideaKey).on('value', snap2 => {
                                i++
                                if(snap2.exists()){ // there might be references to deleted ideas under /team/XYZ/ideas
                                    let idea = Object.assign(this.emptyIdea,snap2.val())
                                    let alreadyOnIdea = false
                                    if(idea.hasOwnProperty('responsible') && idea.responsible.email === authUser.email){
                                        alreadyOnIdea = true
                                    }

                                    if (idea.hasOwnProperty('owner') && idea.owner === authUser.email){
                                        alreadyOnIdea = true
                                    }

                                    if(idea.hasOwnProperty('team')){
                                        idea.team.forEach(aTeam => {
                                            if (aTeam.hasOwnProperty('userId')) {
                                                if (aTeam.userId === authUser.uid){
                                                    alreadyOnIdea = true
                                                }
                                            }
                                        })
                                    }
                                    if(alreadyOnIdea !== true){
                                        if(idea.hasOwnProperty('status')){
                                            thing.storedTeamSharedIdeas = Object.assign({}, thing.storedTeamSharedIdeas, {
                                                [ideaKey]: idea,
                                            })    
                                        } else {
                                            thing.teamSharedIdeas = Object.assign({}, thing.teamSharedIdeas, {
                                                [ideaKey]: idea,
                                            })   
                                        } 
                                    }
                                    else {
                                        if(this.teamSharedIdeas.hasOwnProperty(ideaKey)){
                                            delete this.teamSharedIdeas[ideaKey]
                                        } else if (this.storedTeamSharedIdeas.hasOwnProperty(ideaKey)){
                                            delete this.storedTeamSharedIdeas[ideaKey]
                                        }
                                    }
                                }
                                if (i === numIdeas) {
                                    thing.loadingTeamSharedIdeas = false
                                }
                            })
                        }else{
                            i++
                            if(i === numIdeas){
                                thing.loadingTeamSharedIdeas = false
                            }
                        }
                    }
                }, err => {
                    console.warn(err)
                    this.loadingTeamSharedIdeas = false
                })
            } else {
                this.ownedIdeas = {}
                this.sharedIdeas = {}
                this.storedOwnedIdeas = {}
                this.storedSharedIdeas = {}
                this.storedResponsibleIdeas = {}
                this.subscribeToMyIdeas()
                this.subscribeToIdeasSharedWithMePersonally()
                this.loadingTeamSharedIdeas = false
            }
        })
    }
    subscribeToAllIdeas(){
        this.subscribeToIdeasSharedWithTeam()
    }
    loadIdeaWithKey(ideaKey){
        // console.log('loadIdeaWithKey',ideaKey)
        const authUser = this.authStore.authUser()
        const rootRef = firebase.database().ref()
        let thing = this
        if(!ideaKey) {
            console.log('IdeaKey is missing')
        }
        if (authUser){
            thing.loadingSingleIdea = true
            rootRef.child('ideas/'+ideaKey).on('value', snap => {
                thing.loadingSingleIdea = false
                if(snap.exists()){
                    // console.log('//found idea and the user owns it!')
                    thing.theIdea = Object.assign(thing.emptyIdea,snap.val())
                    thing.theIdeaStatus = 200
                    thing.ideaKey = ideaKey
                    thing.listOfIdeaKeys.push(ideaKey)
                }else{
                    console.warn('loadIdeaWithKey.on() did not return any hits for',ideaKey)
                    thing.theIdeaStatus = 404
                }
            }, err => {
                console.warn(err)
                console.log('authUser', authUser)
                thing.theIdeaStatus = 403
            })
        }else{
            console.error('cannot loadIdeaWithKey without authUser')
            thing.theIdeaStatus = 500
        }
    }
    unloadIdea(ideaKey){
        //send notifications now, if a timer is running
        if(this.theIdeaLastEditTimer){
            clearTimeout(this.theIdeaLastEditTimer)
            this.theIdeaLastEditTimer = null
            const authUser = this.authStore.authUser()
            this.notificationStore.createNotificationToTeamMembers(ideaKey, authUser.displayName + ' updated the idea', this.theIdea.owner, toJS(this.theIdea.team), this.theIdea.responsible, null)
        }
        //TODO: Currently these two commented out lines remove all listeners for 'ideas/'+ideaKey,
        //      this explains the reason why the idea list remains old after idea is changed.
        //      Come with a solution that would use a list instead and update it directly
        //      and then unload it when the user logs off.
        //const rootRef = firebase.database().ref()
        //rootRef.child('ideas/'+ideaKey).off()
        this.flushCurrentIdea()
        this.theIdeaStatus = 0
    }
    flushCurrentIdea(){
        this.theIdea = this.emptyIdea
        this.sketch = null
        this.sketchChecksum = ''
    }
    handleFilterChange(filterText){
        if (filterText === this.prefStore.listFilterMode){
            this.prefStore.listFilterMode = listFilterModes.allIdeas
        } else {
            this.prefStore.listFilterMode = parseFloat(filterText)
        }
        Logger.trackEvent('handleFilterChange','listFilterMode',this.prefStore.listFilterMode)
        this.prefStore.storeUserPreferences()
    }
    handleStoredFilterChange(filterText){
        //this.prefStore.listFilterMode('this.prefStore.listFilterMode1', this.prefStore.listFilterMode)
        if (filterText === this.prefStore.storedListFilterMode){
            this.prefStore.storedListFilterMode = listFilterModes.allIdeas
        } else {
            this.prefStore.storedListFilterMode = parseFloat(filterText)
        }
        Logger.trackEvent('handleStoredFilterChange','storedListFilterMode',this.prefStore.storedListFilterMode)
        this.prefStore.storeUserPreferences()
    }
    returnFilterTag(){
        if (this.prefStore.listFilterMode === listFilterModes.workOn){
            return 'Ideas I work on'
        }
        else if (this.prefStore.listFilterMode === listFilterModes.responsibleFor){
            return 'Ideas I am responsible for'
        }
        else if (this.prefStore.listFilterMode === listFilterModes.teamIdeas){
            return 'Ideas I do not work on'
        }
    }
    returnStoredFilterTag(){
        if (this.prefStore.storedListFilterMode === listFilterModes.workOn){
            return 'Ideas I work on'
        }
        else if (this.prefStore.storedListFilterMode === listFilterModes.responsibleFor){
            return 'Ideas I am responsible for'
        }
        else if (this.prefStore.storedListFilterMode === listFilterModes.teamIdeas){
            return 'Ideas I do not work on'
        }
    }
    handleSortChange(sortType){
        this.prefStore.dueDateMode = sortType
        Logger.trackEvent('handleSortChange','dueDateMode',this.prefStore.dueDateMode)
        this.prefStore.storeUserPreferences()
    }
    returnSortTag(){
        if (this.prefStore.dueDateMode === dueDateModes.byMonth){
            return 'By month'
        }
        else if (this.prefStore.dueDateMode === dueDateModes.byYear){
            return 'By year'
        }
    }
    deleteFilterTag(){
        this.prefStore.listFilterMode = 0
        this.prefStore.storeUserPreferences()
        Logger.trackEvent('deleteFilterTag')
    }
    deleteSearchTag(){
        this.prefStore.listSearchText = ''
        this.prefStore.storeUserPreferences()
        Logger.trackEvent('deleteSearchTag')
    }
    deleteStoredFilterTag(){
        this.prefStore.storedListFilterMode = 0
        this.prefStore.storeUserPreferences()
        Logger.trackEvent('deleteFilterTag')
    }
    deleteStoredSearchTag(){
        this.prefStore.storedListSearchText = ''
        this.prefStore.storeUserPreferences()
        Logger.trackEvent('deleteStoredSearchTag')
    }
    deleteSortTag(){
        this.prefStore.dueDateMode = 0
        this.prefStorestoreUserpreferences()
        Logger.trackEvent('deleteSortTag')
    }
    logout(){
        const authUser = this.authStore.authUser()
        const rootRef = firebase.database().ref()

        if (authUser){
            console.log('.off()-ing!')
            rootRef.child('ideas').off()
            rootRef.child('users/'+authUser.uid+'/myIdeas').off()
            rootRef.child('users/'+authUser.uid+'/ideas').off()
            rootRef.child('teams/' + this.authStore.userProfile().primaryTeam + '/ideas').off()
            rootRef.child('ideas').orderByChild("owner").equalTo(authUser.email).off()
            rootRef.child('users/'+authUser.uid+'/ideas').orderByKey().off()
            for (let i = 0; i < this.listOfIdeaKeys.length; i++){
                rootRef.child('ideas/' + this.listOfIdeaKeys[i]).off()
            }
            if(this.authStore.userProfile() && this.authStore.userProfile().hasOwnProperty('primaryTeam'))
                rootRef.child('teams/'+this.authStore.userProfile().primaryTeam).off()
            rootRef.child('notifications/'+authUser.uid).off()
        }

        this.ownedIdeas = {}
        this.sharedIdeas = {}
        this.teamSharedIdeas = {}
        this.ideas = {}
        this.storedOwnedIdeas = {}
        this.storedSharedIdeas = {}
        this.storedTeamSharedIdeas = {}
        this.storedResponsibleIdeas = {}
    }
    //C-R-U-D IDEAS
    addIdea(authUser){
        var newIdea = this.emptyIdea
        newIdea.owner = authUser.email
        newIdea.ownerName = authUser.displayName
        newIdea.ownerPhotoURL = authUser.photoURL
        newIdea.timestamp = firebase.database.ServerValue.TIMESTAMP
        newIdea.teamId = this.authStore.primaryTeam().key

        var newIdeaKey = firebase.database().ref('ideas/').push(helpers.cleanObj(newIdea)).key
        this.newIdea = true
        firebase.database().ref('users/'+authUser.uid+'/myIdeas/').update({[newIdeaKey]:true})
        this.subscribeToMyIdeas()
        return newIdeaKey
    }
    duplicateIdea(srcIdea, srcIdeaKey){
        const rootRef = firebase.database().ref()
        var newIdea = toJS(srcIdea)
        newIdea.name = srcIdea.name + "(duplicate)"
        newIdea.tags = toJS(srcIdea.tags)
        newIdea.timestamp = firebase.database.ServerValue.TIMESTAMP
        return new Promise(function (resolve, reject) {
            var newIdeaKey = rootRef.child('ideas').push(newIdea).key

            if(srcIdea.hasOwnProperty('team')){
                newIdea.team = toJS(srcIdea.team)
                srcIdea.team.forEach((teamMember) => {
                    if(teamMember.hasOwnProperty('userId')){
                        rootRef.child('users/'+teamMember.userId+'/ideas/').update({[newIdeaKey]:true})
                    }
                    else if(teamMember.hasOwnProperty('teamId')){
                        rootRef.child('teams/'+teamMember.teamId+'/ideas/').update({[newIdeaKey]:true})
                    }
                })
            }
            if(newIdea.hasOwnProperty('responsible')){
                rootRef.child('users/'+newIdea.responsible.userId+'/ideas/').update({[newIdeaKey]:true})
            }
            const params = ['imageUrl', 'needImageUrl','imageUrls','attachmentUrls']
            let dupeCmds = {}
            params.forEach(paramName => {
                if(newIdea.hasOwnProperty(paramName+'_fullPath')){
                    let buckets = []
                    let fullPaths = []

                    if(Array.isArray(newIdea[paramName+'_bucket'])){
                        newIdea[paramName+'_bucket'].forEach(aBucket => {
                            buckets.push(aBucket)
                        })
                    }else{
                        buckets.push(newIdea[paramName+'_bucket'])
                    }
                    if(Array.isArray(newIdea[paramName+'_fullPath'])){
                        newIdea[paramName+'_fullPath'].forEach(aFullPath => {
                            fullPaths.push(aFullPath)
                        })
                    }else{
                        fullPaths.push(newIdea[paramName+'_fullPath'])
                    }
                    fullPaths.forEach((aPath, index) => {
                        if(!dupeCmds.hasOwnProperty(paramName)){
                            dupeCmds[paramName] = []
                        }
                        if(buckets.length > index){
                            dupeCmds[paramName].push(
                                {'bucket':buckets[index],
                                'fullPath':aPath,
                            })
                        }else{
                            console.error('array mismatch!')
                        }
                    })
                }
            })
            rootRef.child('doDupe').update({
                [newIdeaKey]:dupeCmds,
            })

            if(srcIdea.hasOwnProperty('gotSketch')){
                const sketchRef = rootRef.child('sketches')
                sketchRef.child(srcIdeaKey).on('value', snap => { //TODO: should be .once
                    for (var key in snap.val()) {
                        if(snap.val().hasOwnProperty(key)){
                            sketchRef.child(newIdeaKey).set({'sketch':toJS(snap.val()[key])})
                        }
                    }
                    sketchRef.child(srcIdeaKey).off()
                })
            }
            firebase.database().ref().child('users').orderByChild("email").equalTo(newIdea.owner).once('value', function (snap) {
                if (snap.exists()) {
                    rootRef.child('users/'+Object.keys(snap.val())[0]+'/myIdeas/').update({[newIdeaKey]:true})
                    resolve(newIdeaKey)
                }
            })
        })
    }
    storeTheIdea(ideaKey, status){
        const rootRef = firebase.database().ref()
        let thing = this
        return new Promise(function (resolve, reject) {
            rootRef.child('ideas/' + ideaKey + '/status').set(status)
            rootRef.child('ideas/' + ideaKey + '/statusTimestamp').set(firebase.database.ServerValue.TIMESTAMP)
            if(thing.ownedIdeas.hasOwnProperty(ideaKey)){
                delete thing.ownedIdeas[ideaKey]
            }
            if(thing.sharedIdeas.hasOwnProperty(ideaKey)){
                delete thing.sharedIdeas[ideaKey]
            }
            if(thing.teamSharedIdeas.hasOwnProperty(ideaKey)){
                delete thing.teamSharedIdeas[ideaKey]
            }
            resolve(ideaKey)
        })
    }
    reactivateTheIdea(ideaKey){
        const rootRef = firebase.database().ref()
        let thing = this
        return new Promise(function (resolve, reject) {
            rootRef.child('ideas/' + ideaKey + '/status').remove()
            rootRef.child('ideas/' + ideaKey + '/statusTimestamp').remove()
            if(thing.storedOwnedIdeas.hasOwnProperty(ideaKey)){
                delete thing.storedOwnedIdeas[ideaKey]
            }
            if(thing.storedSharedIdeas.hasOwnProperty(ideaKey)){
                delete thing.storedSharedIdeas[ideaKey]
            }
            if(thing.storedTeamSharedIdeas.hasOwnProperty(ideaKey)){
                delete thing.storedTeamSharedIdeas[ideaKey]
            }
            resolve(ideaKey)
        })
    }
    deleteIdea(ideaKey){
        var rootRef = firebase.database().ref()
        const {theIdea} = this
        rootRef.child('ideas/'+ideaKey).off()
        if(this.ownedIdeas.hasOwnProperty(ideaKey)){
            delete this.ownedIdeas[ideaKey]
        }
        if(this.sharedIdeas.hasOwnProperty(ideaKey)){
            delete this.sharedIdeas[ideaKey]
        }
        if(this.teamSharedIdeas.hasOwnProperty(ideaKey)){
            delete this.teamSharedIdeas[ideaKey]
        }

        /* var rootRef = firebase.database().ref()
        //const {theIdea} = this
        let theUser = this.authStore.authUser()
        console.log('user id:', theUser.uid)
        rootRef.child('ideas').child(ideaKey).child('responsible').child('userId').once('value', membersSnap => {
            if(membersSnap.val() === theUser.uid){
                console.log('it matches', membersSnap.val())
            }
            else {
                console.log("it doesn't match", membersSnap.val(), theUser.uid)
            }
        }) */
        //send notifications to the team members
        this.removeSketch(ideaKey, true)
        //Remove under /messages
        rootRef.child('messages/'+ideaKey).remove().then(function () {
        }).catch(function (error) {
            // Uh-oh, an error occurred!
            console.error(error)
        })
        //Remove under /sketches
        rootRef.child('sketches/'+ideaKey).remove().then(function () {
        }).catch(function (error) {
            // Uh-oh, an error occurred!
            console.error(error)
        })
        rootRef.child('ideas/'+ideaKey).remove().then(() => {
            this.notificationStore.createNotificationToTeamMembers('/', this.authStore.authUser().displayName + ' deleted this idea.', this.theIdea.owner, toJS(this.theIdea.team), this.theIdea.responsible, null)

            //TODO: optimize
            //remove references under /users in team-members
            rootRef.child('users').once('value').then(snap => {
                for(var u in snap.val()){
                    var user = snap.val()[u]
                    if (user.ideas){
                        if(user.ideas.hasOwnProperty(ideaKey)){
                            //delete references in teams to this idea
                            rootRef.child('users/'+u+'/ideas/'+ideaKey).remove()
                            //rootRef.child('ideas/'+ideaKey).off()
                        }
                    }
                }
            })
            //delete reference in /team/XXX/ideas
            if(theIdea.hasOwnProperty('teamId')){
                let teamKey = theIdea.teamId
                rootRef.child('teams/'+teamKey+'/ideas/'+ideaKey).remove()
            }

            //delete linked files in storage
            firebase.database().ref().child('users').orderByChild("email").equalTo(theIdea.owner).once('value', function (snap) {
                if (snap.exists()) {
                    rootRef.child('users/'+Object.keys(snap.val())[0]+'/myIdeas/'+ideaKey).remove()
                }
            })
            var pathsOfFiles = [].concat(toJS(theIdea.imageUrls_fullPath)).concat(toJS(theIdea.attachmentUrls_fullPath)).concat(theIdea.imageUrl_fullPath).concat(theIdea.needImageUrl_fullPath)
            for(let i in pathsOfFiles){
                if(pathsOfFiles[i]){
                    Logger.trackEvent('IdeaDelete', ideaKey, pathsOfFiles[i])
                    firebase.storage().ref().child(pathsOfFiles[i]).delete().then(function () {
                        // File deleted successfully
                        // console.log('File deleted successfully:',pathsOfFiles[i])
                    }).catch(function (error) {
                        // Uh-oh, an error occurred!
                        console.error(error)
                    })
                }
            }
        }).catch(function (error) {
            // Uh-oh, an error occurred!
            console.error(error)
        })
    }
    updateIdeaProp(ideaKey, prop, value, updateTimestamp=true){
        if(!ideaKey){
            console.error('updateIdeaProp called without ideaKey')
            return
        }
        const ideaRef = firebase.database().ref().child('ideas/'+ideaKey)
        const teamRef = ideaRef.child('teamId')
        const authUser = this.authStore.authUser()
        this.saving = true
        let thing = this
        //Convert MobX arrays to JS, with no empties for firebase storage
        if(isObservableArray(value)){
            value = helpers.cleanObj(toJS(value))
        }
        setTimeout(() => {
            this.savingRichField = true
        },1)

        if(this.theIdea){
            this.theIdea[prop] = value
            if (this.authStore.primaryTeam().hasOwnProperty('key')
            && !this.theIdea.hasOwnProperty('teamId') && authUser.email === this.theIdea.owner){
                teamRef.set(this.authStore.primaryTeam().key) //set the teamId of idea that do not have it set yet
            }
        }
        Logger.trackEvent('ideaUpdated', 'prop', prop)
        // Logger.trackEvent('ideaUpdated', 'ideaKey', ideaKey)
        /* if (updateTimestamp){
            updatedRef.set(firebase.database.ServerValue.TIMESTAMP)
        } */

        let ideaData = {}
        if (updateTimestamp){
            ideaData['lastUpdated'] = firebase.database.ServerValue.TIMESTAMP
        }
        ideaData[prop] = value
        return ideaRef.update(ideaData).then(ret => {
            if(thing.theIdea){ //if the idea is loaded in IdeaPage
                if(this.savingTimer)
                    clearTimeout(this.savingTimer)
                this.savingTimer = setTimeout(() => {
                    this.saving = false
                    this.savingRichField = false
                },300)
                //send notifications, after a timeout
                if(thing.theIdeaLastEditTimer)
                    clearTimeout(thing.theIdeaLastEditTimer)
                thing.theIdeaLastEditTimer = setTimeout(() => {
                    thing.notificationStore.createNotificationToTeamMembers(ideaKey, authUser.displayName + ' shared some great insights', thing.theIdea.owner, toJS(thing.theIdea.team), thing.theIdea.responsible, null)
                },15*60*1000)
            }
        })
    }
    shareWithTeam(team, ideaKey){
        let ideaTeam = {fullName: team.name, teamId: team.key, photoURL: team.photoURL}
        firebase.database().ref().child('ideas/'+ideaKey+'/team/0').set(ideaTeam)
    }
    updateSlideSettings(list, key){
        const ideaRef = firebase.database().ref().child('ideas/'+key)
        const slidesRef = ideaRef.child('slides')
        var cleanList = []
        for (var elem of list) {
            let newElem = {name: elem.name, show: elem.show}
            cleanList.push(newElem)
        }
        if(this.theIdea)
            this.theIdea.slides = cleanList
        slidesRef.set(cleanList)
        // console.log(this.theIdea.slides[2])
    }
    //MEMBERS & RESPONSIBLE
    addResponsible(ideaKey, email, fullName){
        var thing = this
        var photoURL = INVITEE_PHOTOURL
        const authUser = this.authStore.authUser()

        //filter Idiot owners adding themselves to an idea!
        if(email === this.theIdea.owner){
            return
        }

        var isAlreadyUser = false
        let userId = 0
        //TODO: Here /users/... is referenced by non-current-user, maybe move to cloud-function?
        firebase.database().ref().child('users').orderByChild("email").equalTo(email).once('value', function (snap) {
          if (snap.exists()) {
            console.log('user ' + email + ' exists!')
            //add the <newIdeaKey>:true
            var user = Object.values(snap.val())[0]
            userId = Object.keys(snap.val())[0]
            if(user){
                isAlreadyUser = true
                if(user.hasOwnProperty('photoURL')){
                    photoURL = user.photoURL
                }
                var ideas = user.ideas ? user.ideas : {}
                ideas[ideaKey] = true
                user.ideas = ideas
                snap.ref.update({[userId]:user})
                thing.notificationStore.createNotification(ideaKey, userId, authUser.displayName + ' has requested that you become responsible to the idea.')
                firebase.database().ref().child('ideas/'+ideaKey+'/lastUpdated').set(firebase.database.ServerValue.TIMESTAMP)
            }
          } else {
            console.log('user ' + email + ' does not exist!')
          }
          console.log('adding /invite')
          var newInvite = {
              recipient: email,
              host: authUser.email,
              hostName: authUser.displayName,
              inviteCreated: firebase.database.ServerValue.TIMESTAMP,
              fullName: fullName,
              existingUser: isAlreadyUser,
              ideaName: thing.theIdea.name,
          }
          newInvite['ideaKey'] = ideaKey
          firebase.database().ref().child('invites').push(newInvite)
      }).then(ret => {
            var responsibleUser = {email:email, fullName:fullName, photoURL:photoURL, userId:userId}
            firebase.database().ref().child('ideas/'+ideaKey+'/responsible/').update(helpers.cleanObj(responsibleUser))
        })
    }
    canJoinTheIdea(ideaTeam, userId){
        let canJoin = true
        ideaTeam.forEach(teamMember => {
            if(teamMember.hasOwnProperty('userId')){
                if(teamMember.userId === userId){
                    canJoin = false
                }
            }
        })
        return canJoin
    }
    isPartOfTheIdea(theIdea, theUser){
        let canView = false
        if(theUser){
            if(theIdea.hasOwnProperty('team')){
                theIdea.team.forEach(teamMember => {
                    if(teamMember.hasOwnProperty('userId')){
                        if(teamMember.userId === theUser.uid){
                            canView = true
                        }
                    }
                })
            }
            if(theIdea.owner === theUser.email){
                canView = true
            }
            if(theIdea.hasOwnProperty('responsible') && theIdea.responsible.userId === theUser.uid){
                canView = true
            }
        }
        return canView
    }
    addMemberToIdea(ideaKey, email, fullName, ownerEmail){
        var thing = this
        var photoURL = INVITEE_PHOTOURL
        const authUser = this.authStore.authUser()

        //filter Idiot owners adding themselves to an idea!
        if(email === ownerEmail){
            return
        }

        var isAlreadyUser = false
        let userId = 0
        //TODO: Here /users/... is referenced by non-current-user, maybe move to cloud-function?
        firebase.database().ref().child('users').orderByChild("email").equalTo(email).once('value', function (snap) {
          if (snap.exists()) {
            console.log('user ' + email + ' exists!')
            //add the <newIdeaKey>:true
            var user = Object.values(snap.val())[0]
            userId = Object.keys(snap.val())[0]
            if(user){
                isAlreadyUser = true
                if(user.hasOwnProperty('photoURL')){
                    photoURL = user.photoURL
                }
                var ideas = user.ideas ? user.ideas : {}
                ideas[ideaKey] = true
                user.ideas = ideas
                snap.ref.update({[userId]:user})
                thing.notificationStore.createNotification(ideaKey, userId, authUser.displayName + ' has requested that you join the idea.')
                firebase.database().ref().child('ideas/'+ideaKey+'/lastUpdated').set(firebase.database.ServerValue.TIMESTAMP)
            }
          } else {
            console.log('user ' + email + ' does not exist!')
            var newUserInvite = {
                email: email,
                ideaKey: ideaKey,
            }
            firebase.database().ref().child('newUserInvites').push(newUserInvite)
          }
        if(authUser.email !== email){
            console.log('adding /invite')
            var newInvite = {
                recipient: email,
                host: authUser.email,
                hostName: authUser.displayName,
                inviteCreated: firebase.database.ServerValue.TIMESTAMP,
                fullName: fullName,
                existingUser: isAlreadyUser,
                ideaName: thing.theIdea.name,
            }
            newInvite['ideaKey'] = ideaKey
            firebase.database().ref().child('invites').push(newInvite)
        }
      }).then(ret => {
            var shareList = toJS(this.theIdea.team)
            if(shareList){
                let count = shareList.length

                var found = false
                for(var member in shareList){
                    if(shareList[member].hasOwnProperty('email')){
                        if(shareList[member].email===email){
                            found = true
                        }
                    }
                }

                if(!found){ //do not add emails already in list
                    shareList[count] = {email:email, fullName:fullName, photoURL:photoURL, userId:userId} //append at end
                }
            }else{
                shareList = [{email:email, fullName:fullName, photoURL:photoURL, userId:userId}]
            }
            firebase.database().ref().child('ideas/'+ideaKey+'/team/').update(helpers.cleanObj(shareList))
        })
    }
    removeMemberFromIdeaTeam(memberEmail, ideaKey, sharedDeleteType){
        let thing = this
        let loadedTeam = this.prefStore.ideaTeam
        //TODO: Here /users/... is referenced by non-current-user, maybe move to cloud-function?
        //remove from /users/UID/ideas/IDEAkey
        const usersRef = firebase.database().ref().child('users')
        let teamRef = null
        if (sharedDeleteType === 'responsible'){
            teamRef = firebase.database().ref().child('ideas/'+ideaKey+'/responsible/')
        }
        else {
            teamRef = firebase.database().ref().child('ideas/'+ideaKey+'/team/')
        }
        //remove from /ideas/IDEAkey/team/EMAIL
        return teamRef.once('value', function (snap) {
            var exists = (snap.val() !== null)
            if (exists) {
                // console.log('Whole snap', snap.val())
                if (sharedDeleteType === 'responsible' && thing.authStore.authUser().email === memberEmail){
                    thing.notificationStore.createNotificationToTeamMembers(ideaKey, thing.authStore.authUser().displayName + ' has left the idea', thing.theIdea.owner, toJS(thing.theIdea.team), thing.theIdea.responsible, loadedTeam)
                    snap.ref.remove()
                }
                else if (sharedDeleteType === 'responsible' && thing.authStore.authUser().email !== memberEmail){
                    thing.notificationStore.createNotificationToTeamMembers(ideaKey, thing.authStore.authUser().displayName + ' unshared this idea with ' + memberEmail, thing.theIdea.owner, toJS(thing.theIdea.team), thing.theIdea.responsible, loadedTeam)
                    snap.ref.remove()
                }
                else{
                    thing.notificationStore.createNotificationToTeamMembers(ideaKey, thing.authStore.authUser().displayName + ' unshared this idea with ' + memberEmail, thing.theIdea.owner, toJS(thing.theIdea.team), thing.theIdea.responsible, loadedTeam)
                    for(let i in snap.val()){
                        let member = Object.values(snap.val())[i]
                        let memberIndex = Object.keys(snap.val())[i]
                        if(member.email === memberEmail){
                            // console.log('Found user on team', memberEmail, memberIndex)
                            var newTeam = snap.val().slice() //copy array

                            newTeam.splice(memberIndex, 1) //remove element
                            // console.log('New team', newTeam)
                            //eslint-disable-next-line no-loop-func

                            if (newTeam.length > 0){
                                snap.ref.set(newTeam)
                            } else {
                                snap.ref.remove()
                            }
                        }
                    }
                }
            }
        }).then(ret => {
            usersRef.orderByChild("email").equalTo(memberEmail).once('value', function (snap) {
                var exists = (snap.val() !== null)
                var ideas = null
                if (exists) {
                    var user = Object.values(snap.val())[0]
                    var userKey = Object.keys(snap.val())[0]
                    if(user){
                        if(user.ideas){
                            firebase.database().ref().child('ideas/'+ideaKey).once('value', function (ideaSnapshot) {
                                if (ideaSnapshot.exists()) {
                                    let idea = ideaSnapshot.val()
                                    let found = false
                                    if (idea.hasOwnProperty('responsible') && idea.responsible.email === memberEmail){
                                        found = true
                                        return
                                    }
                                    else if (idea.hasOwnProperty('team')){
                                        idea.team.forEach(aTeam => {
                                            if (aTeam.hasOwnProperty('userId')) {
                                                if (aTeam.email !== memberEmail) {
                                                    found = true
                                                    return
                                                }
                                            }
                                        })
                                    }
                                    if(found === false){
                                        ideas = user.ideas
                                        delete ideas[ideaKey]
                                        user.ideas = ideas
                                        return snap.ref.update({[userKey]:user})
                                    }
                                } else {
                                    ideas = user.ideas
                                    delete ideas[ideaKey]
                                    user.ideas = ideas
                                    return snap.ref.update({[userKey]:user})
                                }
                            })
                        }else{
                            console.error('no such idea found on this user!')
                        }
                    }
                }else{
                    console.error('user not found!')
                }
            }).then(a => {
                firebase.database().ref().child('ideas/'+ideaKey+'/lastUpdated').set(firebase.database.ServerValue.TIMESTAMP)
                if(this.authStore.authUser().email === memberEmail){
                    //if the current user is removed (Leaving) a team, then reload the shared ideas
                    setTimeout(() => {
                        this.subscribeToIdeasSharedWithMePersonally()
                    }, 800) //HACK: but looks super cool
                }
            })
        })
    }
    //SKETCHES
    loadSketchForIdea(ideaKey){
        const rootRef = firebase.database().ref()
        rootRef.child('sketches/'+ideaKey).limitToLast(1).on('value', snap => {
            var lastSketch = null
            for (var key in snap.val()) {
                if(snap.val().hasOwnProperty(key)){
                    lastSketch = toJS(snap.val()[key])
                }
            }
            this.sketch = lastSketch
            this.sketchChecksum = helpers.checksum(lastSketch)
            //console.log('Sketch loaded!',helpers.checksum(lastSketch))
        }, null, this)
    }
    unloadSketches(ideaKey){
        this.sketch = {}
        const rootRef = firebase.database().ref()
        rootRef.child('sketches/'+ideaKey).off()
    }
    newSketch(sketch, sketchPNG, ideaKey){
        const sketchesRef = firebase.database().ref().child('sketches/')

        //TODO: UGLY hack to avoid shapes getting black fill, introduced in early july 2017
        //react-sketch component returns paths with "fill: null", but fields with null are
        // ...not stored in Firebase, and then when reloaded a black fill is set and then stored

        // console.log('newSketch pre-clean:',sketch)
        for(var i in sketch){
            for(var j in sketch[i]){
                // console.log('sketch[i]',sketch[i])
                // console.log('sketch[i][j]',sketch[i][j])
                if(sketch[i][j].hasOwnProperty('fill')){
                    if(sketch[i][j].fill === null){
                        sketch[i][j].fill = 'rgba(0,0,0,0)'
                    }
                }
                if(sketch[i][j].hasOwnProperty('top')){
                    if(sketch[i][j].top === Infinity){
                        console.log('newSketch() hit top=Infinity issue!')
                        return
                    }
                    if(isNaN(sketch[i][j].top)){
                        console.log('newSketch() hit top=NaN issue!')
                        return
                    }
                }
            }
        }
        // console.log('newSketch post-replace:',sketch)

        var ns = helpers.cleanObj(toJS(sketch))
        // console.log('newSketch post-clean:',ns)
        // this.sketchChecksum = helpers.checksum(sketch)
        //console.log('newSketch',helpers.checksum(sketch),helpers.checksum(ns))
        sketchesRef.child(ideaKey).set({'sketch':ns}) //.then(
            // console.log('sketch stored'),
        //)

        var storageRef = firebase.storage().ref()
        var sketchesStorageRef = storageRef.child('ideas/ideasheet/sketchPNGs')
        var sketchPNGRef = sketchesStorageRef.child(ideaKey+'.png')

        var the_file = helpers.dataURItoBlob(sketchPNG)

        sketchPNGRef.put(the_file).then(snapshot => {
            // console.log('Uploaded sketchPNG file!')
            sketchPNGRef.getDownloadURL().then(url => {
                firebase.database().ref().child('ideas/'+ideaKey+'/gotSketch').set(url)
                // console.log('Got sketchPNG url:'+url)
            }).then(() => {
            })
        })
    }
    removeSketch(ideaKey, removeGotSketch){
        let thing = this
        var rootRef = firebase.database().ref()
        if (this.theIdea.gotSketch !== ''){
            rootRef.child('sketches/'+ideaKey).remove().then(()=> {
                if(thing.theIdea.hasOwnProperty('gotSketch')){
                    firebase.storage().refFromURL(thing.theIdea.gotSketch).delete()
                }
            }).then(()=> {
                if (removeGotSketch === false){
                    return thing.updateIdeaProp(
                        ideaKey,
                        'gotSketch',
                        '',
                    )
                }
                else {
                    return
                }
            }).then(()=> {
                thing.sketch = null
                thing.sketchChecksum = ''
            })
        }
    }
    addDueDateToDb(ideaKey, dueDate){
        var rootRef = firebase.database().ref()
        rootRef.child('ideaDueDates/' + ideaKey).set({dueDate: dueDate})
    }
}

export default IdeaStore



// WEBPACK FOOTER //
// ./src/stores/IdeaStore.js